import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

const ToastContext = createContext(() => {});

/** Émet un message : `toast('Devis généré')`, ou `toast(msg, 'error')`. */
export function useToast() {
  return useContext(ToastContext);
}

let seq = 0;

/**
 * Pile de messages éphémères. Ils s'empilent au lieu de se remplacer : deux
 * tâches qui se terminent à trois secondes d'intervalle méritent deux avis, pas
 * un seul qui écrase l'autre.
 *
 * `role="status"` et `aria-live="polite"` : un lecteur d'écran annonce le
 * message sans interrompre ce que la personne est en train de lire.
 */
export default function ToastProvider({ children }) {
  const [items, setItems] = useState([]);
  const timers = useRef(new Map());

  const dismiss = useCallback((id) => {
    setItems((list) => list.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) { clearTimeout(timer); timers.current.delete(id); }
  }, []);

  const push = useCallback((message, tone = 'info', ms = 5000) => {
    if (!message) return;
    seq += 1;
    const id = seq;
    setItems((list) => [...list.slice(-3), { id, message, tone }]);
    timers.current.set(id, setTimeout(() => dismiss(id), ms));
  }, [dismiss]);

  const value = useMemo(() => push, [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-stack" role="status" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className={`toast ${t.tone}`}>
            <span className="toast-msg">{t.message}</span>
            <button
              type="button"
              className="toast-close"
              onClick={() => dismiss(t.id)}
              aria-label="Fermer ce message"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
